import { runInAction } from "mobx";

import { getSuggestionsDoc } from "@/components/suggestions/suggestionsDoc";
import { createNodeFrom } from "@/models/nodes/createNodeFrom";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { isGroupOpeningSymbol, openingToClosingMap } from "@/models/nodes/groups/utils";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

import { groupsJson } from "./data";

import type { SuggestionProvider, Suggestion } from "@/components/suggestions/types";

const openingSymbolKeyMap = Object.values(groupsJson).reduce(
  (acc, gr) => {
    if (gr.openingSymbol.key) {
      acc[gr.openingSymbol.symbol] = gr.openingSymbol.key;
    }
    return acc;
  },
  {} as Record<string, string>,
);

const closingSymbolKeyMap = Object.values(groupsJson).reduce(
  (acc, gr) => {
    if (gr.closingSymbol.key) {
      acc[gr.closingSymbol.symbol] = gr.closingSymbol.key;
    }
    return acc;
  },
  {} as Record<string, string>,
);

export const variableFunctionSuggestionsProvider: SuggestionProvider = {
  type: "variableFunction",
  search: (str, doc, _lang) => {
    const suggestionsDoc = getSuggestionsDoc();
    const suggestions: Suggestion[] = [];
    const { nodeBeforeCursor, node } = doc.cursor;

    if (
      isGroupOpeningSymbol(str) &&
      nodeBeforeCursor?.is(VariableNodeModel) &&
      !nodeBeforeCursor.hasParams
    ) {
      if (doc.selection.hasSelectedNodes) return [];

      const closingSymbol = openingToClosingMap[str];

      // TODO: custom parantheses for functions
      const applyFunctionSuggestion: Suggestion = {
        type: "single",
        icon: "math/ApplyFunction",
        item: {
          type: "variable",
          intlName: "variableFunction.apply",
          symbol: nodeBeforeCursor.value,
          props: {
            openingSymbol: {
              key: openingSymbolKeyMap[str],
              symbol: str,
            },
            closingSymbol: {
              key: closingSymbolKeyMap[closingSymbol],
              symbol: closingSymbol,
            },
            arguments: [],
          },
          insertChildren: [
            [
              {
                type: "inline",
              },
            ],
          ],
        },
        canAutocomplete: true,
        canBeAutocompleted: {
          math: true,
        },
        action: {
          type: "apply-function",
        },
        priority: 1,
      };

      suggestions.push(applyFunctionSuggestion);
    }

    if (str === "," && node?.parent) {
      const { parent } = node;
      const group = parent.parent;
      if (parent.is(MatrixNodeModel) && parent.rows === 1 && group?.is(GroupNodeModel)) {
        const sibling = group.prevSiblings[0];
        if (sibling?.is(VariableNodeModel) && !sibling.hasParams) {
          runInAction(() => {
            const newNode = createNodeFrom(sibling, suggestionsDoc);
            const before = createNodeFrom(node, suggestionsDoc);
            const { index } = doc.cursor;
            const after = suggestionsDoc.create(InlineNodeModel);
            after.appendChildren(before.nodes.slice(index));

            newNode.appendChild(before);
            newNode.appendChild(after);

            const transformToParametersSuggestion: Suggestion = {
              type: "single",
              icon: "math/ApplyFunction",
              item: {
                node: newNode,
                type: "variable",
                displayName: "Add function parameter",
                symbol: newNode.value,
              },
              canAutocomplete: true,
              canBeAutocompleted: {
                math: true,
              },
              action: {
                type: "replace-nodes",
                ids: [group.id, sibling.id],
              },
              substitution: {
                cursorPlacement: `child:1:start`,
              },
              priority: 3,
            };

            suggestions.push(transformToParametersSuggestion);
          });
        }
      }
    }

    return [suggestions];
  },
};
